import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FileSpreadsheet, Upload } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { DatePicker } from '@/shared/ui/date-picker';
import { MessageFieldWithEmoji } from './message-field-with-emoji';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { cn } from '@/shared/lib/cn';
import { saveUploadCampaign } from '../api/use-campaigns';
import { materializeUploadedAudience, serialsFromUpload } from '@/features/segmentacoes/model/materialize-upload';
import { TimePicker } from './time-picker';

interface Props {
  idPassbook: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

/** Form de upload Metabase — espelha metabase_campaign_insert.ejs */
export function UploadCampaignFormDialog({ idPassbook, open, onOpenChange, onCreated }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [disparoDate, setDisparoDate] = useState('');
  const [disparoTime, setDisparoTime] = useState('');
  const [performance, setPerformance] = useState('7');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setDisparoDate('');
      setDisparoTime('');
      setPerformance('7');
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [open]);

  const save = async () => {
    if (!name.trim() || !disparoDate.trim() || !file) {
      toast.error('Preencha nome, data de disparo e selecione o arquivo.');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('name', name);
      fd.append('description', description);
      fd.append('disparo_date', disparoDate);
      fd.append('disparo_time', disparoTime);
      fd.append('performance', performance);
      fd.append('arquivo_metabase', file);
      await saveUploadCampaign(idPassbook, fd);
      const serials = await serialsFromUpload(file);
      const audience = await materializeUploadedAudience({
        idPassbook,
        nome: name.trim(),
        fileName: file.name,
        serials,
      });
      if (!audience.parsed) {
        toast.warning('Audiência criada sem contatos.', {
          description: 'O arquivo não tem uma coluna serial_number em CSV. A lista ficou vazia.',
        });
      } else if (!audience.persisted) {
        toast.warning('Audiência disponível neste navegador.', {
          description: 'O servidor de segmentação não confirmou a gravação. O nome continua o da campanha.',
        });
      } else {
        toast.success('Campanha criada e audiência disponível para as campanhas.');
      }
      await queryClient.invalidateQueries({ queryKey: ['segmentations', idPassbook] });
      onOpenChange(false);
      onCreated();
    } catch {
      toast.error('Falha ao criar campanha.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Nova campanha de upload</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Envie o arquivo CSV exportado do BI com a coluna <code className="text-xs">serial_number</code>.
        </p>
        <div className="space-y-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="up-name">Nome da campanha</Label>
            <Input
              id="up-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Campanha BI março"
            />
          </div>

          <MessageFieldWithEmoji
            id="up-msg"
            label="Mensagem"
            rows={3}
            value={description}
            onChange={setDescription}
            placeholder="Texto da campanha…"
          />

          <div className="grid items-start gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="up-date">Data de disparo</Label>
              <DatePicker
                id="up-date"
                value={disparoDate}
                onChange={setDisparoDate}
                placeholder="Selecione a data"
                className="w-full"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="up-time">Horário</Label>
              <TimePicker
                id="up-time"
                label={null}
                value={disparoTime}
                onChange={setDisparoTime}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label>Performance</Label>
            <Select value={performance} onValueChange={setPerformance}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 30 }, (_, i) => i + 1).map((d) => (
                  <SelectItem key={d} value={String(d)}>
                    {d} {d === 1 ? 'dia' : 'dias'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label>Arquivo (exportado do BI)</Label>
            <input
              ref={fileRef}
              id="up-file"
              type="file"
              accept=".csv,.xlsx,.xls"
              className="sr-only"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {file ? (
              <div className="flex items-center gap-3 rounded-xl border border-border bg-[hsl(var(--glass-bg)/0.4)] px-4 py-3">
                <FileSpreadsheet className="size-5 shrink-0 text-primary" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setFile(null);
                    if (fileRef.current) fileRef.current.value = '';
                    fileRef.current?.click();
                  }}
                >
                  Trocar
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className={cn(
                  'group flex w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-[hsl(var(--glass-bg)/0.4)] py-10 text-center transition-[border-color,background-color] duration-300 ease-out',
                  'hover:border-primary/45 hover:bg-primary/[0.06]',
                )}
              >
                <Upload className="size-8 text-muted-foreground transition-all duration-300 group-hover:scale-105 group-hover:text-primary" />
                <div>
                  <p className="text-sm font-medium">Selecionar arquivo</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">CSV, XLSX ou XLS</p>
                </div>
              </button>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={save} loading={busy}>
            Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
